import { useMemo } from 'react';
import { getProxyImageUrl } from '../services/api';

// Define image size contexts for better organization
export type ImageSizeContext = 
  | 'thumbnail'     // Small thumbnails in grids
  | 'thumbnail-sm'  // Very small thumbnails
  | 'thumbnail-lg'  // Large thumbnails
  | 'card'          // Card images in lists
  | 'medium'        // Medium images for posts
  | 'medium-lg'     // Large medium images
  | 'large'         // Large images for single posts
  | 'hero'          // Hero/banner images
  | 'avatar'        // User avatars
  | 'original';     // Original with optimization

// Map contexts to actual size configurations
const SIZE_CONTEXT_MAP: Record<ImageSizeContext, string> = {
  'thumbnail': 'thumb',
  'thumbnail-sm': 'thumb-sm', 
  'thumbnail-lg': 'thumb-lg',
  'card': 'card',
  'medium': 'medium',
  'medium-lg': 'medium-lg',
  'large': 'large',
  'hero': 'hero',
  'avatar': 'avatar',
  'original': 'original'
};

// Size recommendations based on usage
export const IMAGE_SIZE_RECOMMENDATIONS = {
  // Grid views and lists
  'news-feed-grid': 'medium' as ImageSizeContext, // Upgraded from thumbnail to medium for PC home page
  'tag-page-grid': 'thumbnail' as ImageSizeContext,
  'related-posts-grid': 'thumbnail' as ImageSizeContext, // Changed from thumbnail-sm to thumbnail for better quality
  
  // Card layouts
  'post-card': 'card' as ImageSizeContext,
  'featured-card': 'medium' as ImageSizeContext,
  
  // Single post views
  'single-post-main': 'large' as ImageSizeContext,
  'single-post-hero': 'hero' as ImageSizeContext,
  'single-post-inline': 'medium' as ImageSizeContext,
  
  // Special cases
  'user-avatar': 'avatar' as ImageSizeContext,
  'mobile-thumbnail': 'thumbnail-sm' as ImageSizeContext,
  'mobile-card': 'card' as ImageSizeContext,
  'mobile-hero': 'medium-lg' as ImageSizeContext, // Smaller hero for mobile
};

interface UseOptimizedImageOptions {
  size?: ImageSizeContext;
  context?: keyof typeof IMAGE_SIZE_RECOMMENDATIONS;
  fallbackToOriginal?: boolean;
  isMobile?: boolean;
}

interface OptimizedImageResult {
  src: string;
  originalSrc: string;
  isOptimized: boolean;
  size: string;
  context?: string;
}

/**
 * Custom hook for getting optimized image URLs
 */
export const useOptimizedImage = (
  originalUrl: string,
  options: UseOptimizedImageOptions = {}
): OptimizedImageResult => {
  
  return useMemo(() => {
    if (!originalUrl || typeof originalUrl !== 'string') {
      return {
        src: '',
        originalSrc: originalUrl || '',
        isOptimized: false,
        size: 'none',
        context: options.context
      };
    }

    // Determine the appropriate size
    let targetSize: ImageSizeContext = 'medium'; // default
    
    if (options.size) {
      targetSize = options.size;
    } else if (options.context) {
      targetSize = IMAGE_SIZE_RECOMMENDATIONS[options.context] || 'medium';
      
      // Mobile-specific adjustments
      if (options.isMobile) {
        switch (options.context) {
          case 'single-post-hero':
            targetSize = 'medium-lg'; // Smaller hero for mobile
            break;
          case 'post-card':
            targetSize = 'card'; // Keep card size for mobile
            break;
          case 'news-feed-grid':
            targetSize = 'thumbnail'; // Use thumbnail for mobile news feed (vs medium for PC)
            break;
        }
      }
    }

    // Check if the URL is already a proxy URL
    const isAlreadyProxied = originalUrl.includes('/api/images/proxy/') || originalUrl.includes('/api/media/');
    
    if (isAlreadyProxied && !options.fallbackToOriginal) {
      return {
        src: originalUrl,
        originalSrc: originalUrl,
        isOptimized: false,
        size: 'unknown',
        context: options.context
      };
    }

    // Generate optimized URL
    const actualSize = SIZE_CONTEXT_MAP[targetSize];
    const optimizedUrl = getProxyImageUrl(originalUrl, actualSize);

    return {
      src: optimizedUrl,
      originalSrc: originalUrl,
      isOptimized: true,
      size: actualSize,
      context: options.context
    };
  }, [originalUrl, options.size, options.context, options.fallbackToOriginal, options.isMobile]);
};

/**
 * Hook specifically for responsive images that adapts to screen size
 */
export const useResponsiveImage = (
  originalUrl: string,
  context: keyof typeof IMAGE_SIZE_RECOMMENDATIONS,
  isMobile: boolean = false
): OptimizedImageResult => {
  return useOptimizedImage(originalUrl, {
    context,
    isMobile
  });
};

/**
 * Hook for getting multiple sizes of the same image (for srcset)
 */
export const useMultiSizeImage = (
  originalUrl: string,
  sizes: ImageSizeContext[]
): Record<ImageSizeContext, string> => {
  return useMemo(() => {
    if (!originalUrl || typeof originalUrl !== 'string') {
      return {} as Record<ImageSizeContext, string>;
    }

    const result: Record<ImageSizeContext, string> = {} as Record<ImageSizeContext, string>;
    
    sizes.forEach(size => {
      const actualSize = SIZE_CONTEXT_MAP[size];
      result[size] = getProxyImageUrl(originalUrl, actualSize);
    });

    return result;
  }, [originalUrl, sizes]);
};

/**
 * Utility function to get the best image size based on container dimensions
 */
export const getBestImageSizeForDimensions = (
  containerWidth: number,
  containerHeight: number
): ImageSizeContext => {
  // Simple heuristic based on container size
  if (containerWidth <= 150) {
    return 'thumbnail-sm';
  } else if (containerWidth <= 200) {
    return 'thumbnail';
  } else if (containerWidth <= 300) {
    return 'thumbnail-lg';
  } else if (containerWidth <= 400) {
    return 'card';
  } else if (containerWidth <= 600) {
    return 'medium';
  } else if (containerWidth <= 800) {
    return 'medium-lg';
  } else if (containerWidth <= 1200) {
    return 'large';
  } else {
    return 'hero';
  }
};

export default useOptimizedImage; 